import React, { FC, useState, useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { isMobile } from 'react-device-detect';
// api
import { apiApp } from '../../../api/app';
// actions
import dyopsAction from '../../../redux/actions/dyops';
// types
import { loadingType } from '../../../types/types';
// helpers
import { applyExamShellBackground } from '../../../tools';
import { copyTextToClipboard } from '../../../tools/copyTextToClipboard';
import { readPatientTestType } from '../../../tools/patientOnboardingSelection';
import { chromeSecondaryButtonClassName } from '../../../constants/chromeSecondaryButton';
// style
import './index.scss';
// localization
import strings from '../../../localization';

interface ConnectProps {
  userType: string;
  state?: { testType: string; inputType: string };
  appLoading: loadingType;
}

const Connect: FC<ConnectProps> = ({ userType, state, appLoading }: ConnectProps) => {
  const [roomId, setRoomId] = useState<string>('');
  const [customRoomId, setCustomRoomId] = useState<string>('');
  const [codeCopied, setCodeCopied] = useState(false);
  const copyFeedbackTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const codeInputRef = useRef<HTMLInputElement>(null);

  const dispatch = useDispatch();
  const history = useHistory();

  const getRoomId = () => {
    apiApp.getRoomId().then((res) => setRoomId(res.data.roomId));
  };

  useEffect(() => {
    userType === 'doctor' && getRoomId();
  }, []);

  useEffect(() => {
    return () => {
      if (copyFeedbackTimeoutRef.current) {
        clearTimeout(copyFeedbackTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (userType !== 'patient') {
      return;
    }

    const input = codeInputRef.current;
    if (!input) {
      return;
    }

    const frameId = requestAnimationFrame(() => {
      input.focus({ preventScroll: true });
    });

    return () => cancelAnimationFrame(frameId);
  }, [userType]);

  // Copies the doctor room code and briefly shows copied feedback.
  const handleCopyRoomId = () => {
    if (!roomId) {
      return;
    }

    void copyTextToClipboard(roomId).then((copied) => {
      if (!copied) {
        return;
      }

      setCodeCopied(true);

      if (copyFeedbackTimeoutRef.current) {
        clearTimeout(copyFeedbackTimeoutRef.current);
      }

      copyFeedbackTimeoutRef.current = setTimeout(() => setCodeCopied(false), 2000);
    });
  };

  // Auto-rotate hint is phone-only; desktop web does not need it.
  const showConnectAutoRotateNote = isMobile;

  const handleSubmit = (roomId: string, userType: string) => {
    const callback = () => {
      applyExamShellBackground('admin');
      history.push({
        pathname: userType === 'patient' ? '/test' : '/d_test',
        state:
          userType === 'patient'
            ? state
              ? { ...state, roomId, testType: state.testType || readPatientTestType() }
              : { roomId, testType: readPatientTestType() }
            : state
            ? { ...state, roomId, testType: state.testType }
            : { roomId },
      });
    };

    dispatch(dyopsAction.checkRoomAction(roomId, callback));
  };

  const handleCustomRoomId = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCustomRoomId(e.target.value);
  };

  const startDisabled =
    (userType === 'patient' && customRoomId.length < 6) || customRoomId.length > 6 || appLoading.status;

  /** Submits Start test when the form is valid (supports Enter in the code field). */
  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (startDisabled) {
      return;
    }
    handleSubmit(customRoomId ? customRoomId : roomId, userType || '');
  };

  return (
    <div className="connect">
      <form className="connect__inner" onSubmit={handleFormSubmit}>
        <p className="connect__text">
          {userType === 'doctor' ? strings.connectPage.labels.sendCode : strings.connectPage.labels.enterDoctorCode}
        </p>
        <div className="connect__code-field">
          {userType === 'doctor' && codeCopied && (
            <span className="connect__copy-feedback">{strings.connectPage.labels.codeCopied}</span>
          )}
          <div className="connect__input-wrapper">
            <input
              ref={codeInputRef}
              className="connect__input"
              min="6"
              max="6"
              placeholder={strings.connectPage.labels.enterCode}
              onChange={handleCustomRoomId}
              value={userType === 'doctor' ? roomId : customRoomId}
              type="text"
              disabled={userType === 'doctor'}
              autoComplete="off"
            />
            {userType === 'doctor' && (
              <button
                type="button"
                className={`connect__copy-btn${codeCopied ? ' connect__copy-btn--copied' : ''}`}
                onClick={handleCopyRoomId}
                aria-label={strings.connectPage.labels.copyCode}
              >
                <span className="connect__copy-icon" aria-hidden="true">
                  <svg width="17" height="20" viewBox="0 0 17 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path
                      d="M12.3332 0.833008H2.33317C1.4165 0.833008 0.666504 1.58301 0.666504 2.49967V14.1663H2.33317V2.49967H12.3332V0.833008ZM14.8332 4.16634H5.6665C4.74984 4.16634 3.99984 4.91634 3.99984 5.83301V17.4997C3.99984 18.4163 4.74984 19.1663 5.6665 19.1663H14.8332C15.7498 19.1663 16.4998 18.4163 16.4998 17.4997V5.83301C16.4998 4.91634 15.7498 4.16634 14.8332 4.16634ZM14.8332 17.4997H5.6665V5.83301H14.8332V17.4997Z"
                      fill="currentColor"
                    />
                  </svg>
                </span>
              </button>
            )}
          </div>
        </div>
        <div className="connect__btns">
          {userType === 'doctor' && (
            <button type="button" className={`connect__btn big ${chromeSecondaryButtonClassName}`} onClick={getRoomId}>
              {strings.connectPage.labels.generateNew}
            </button>
          )}
          <button
            type="submit"
            disabled={startDisabled}
            className={`btn btn_submit ${userType === 'patient' ? 'btn_wide' : ''}`}
          >
            {strings.connectPage.labels.startTest}
          </button>
        </div>
      </form>
      {showConnectAutoRotateNote && (
        <p className="connect__text connect__text_warning">{strings.connectPage.labels.note}</p>
      )}
    </div>
  );
};

export default Connect;
